import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  Text,
  tokens,
  useId,
} from '@coreai/fluentui-react';
import { visuallyHiddenStyle } from '@/styles/a11y';
import { destructiveButtonStyle } from '@/styles/destructiveButton';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '480px',
  },
  deleteList: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    margin: `${tokens.spacingVerticalS} 0`,
    paddingLeft: tokens.spacingHorizontalL,
    // Resource names are user-supplied and frequently unbroken (e.g.
    // "CarlosTestApp"), which would otherwise overflow the fixed-width surface.
    overflowWrap: 'break-word',
  },
  // Resource names are interpolated into this sentence. An unbroken name has no
  // break opportunity, so without `break-word` it runs past the 480px surface
  // (measured: 99px of horizontal overflow). `pretty` keeps the closing
  // "undone." from being orphaned on its own line.
  message: {
    overflowWrap: 'break-word',
    textWrap: 'pretty' as const,
  },
  // Fluent's `primary` appearance fights every naive override here; see
  // `destructiveButtonStyle` for the three traps it encodes.
  deleteButton: destructiveButtonStyle,
  srOnly: visuallyHiddenStyle,
});

export type DeleteConfirmDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed (cancel or backdrop click) */
  onClose: () => void;
  /** Callback when the delete action is confirmed */
  onConfirm: () => void;
  /** The type of item being deleted (e.g., "workflow", "application") */
  itemType: string;
  /** Names of items to delete */
  itemNames: string[];
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
};

/**
 * A reusable delete confirmation dialog that supports single or multiple items.
 */
export const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  itemType,
  itemNames,
  isDeleting = false,
}: DeleteConfirmDialogProps) => {
  const styles = useStyles();
  const descriptionId = useId('delete-confirm-desc-');
  const count = itemNames.length;
  const plural = count !== 1;
  const itemLabel = plural ? `${itemType}s` : itemType;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        // Never dismiss mid-flight: the request keeps running regardless, so
        // closing here would strand the user with no feedback. Previously each
        // call site had to remember this guard and only one of the three did.
        if (!data.open && !isDeleting) onClose();
      }}
    >
      <DialogSurface className={styles.dialogSurface} aria-describedby={descriptionId}>
        <DialogBody>
          <DialogTitle>
            Delete {plural ? `${count} ${itemLabel}` : itemLabel}?
          </DialogTitle>
          <DialogContent>
            <Text className={styles.message} id={descriptionId}>
              {plural
                ? `Are you sure you want to delete the following ${itemLabel}? This action cannot be undone.`
                : `Are you sure you want to delete "${itemNames[0]}"? This action cannot be undone.`}
            </Text>
            {plural && (
              <ul className={styles.deleteList}>
                {itemNames.map((name) => <li key={name}>{name}</li>)}
              </ul>
            )}
            {/* The busy state is otherwise silent: the label swap to
                "Deleting..." is not announced, so assistive tech gets no
                confirmation that the action was accepted. */}
            <div role="status" aria-live="polite" className={styles.srOnly}>
              {isDeleting ? `Deleting ${plural ? `${count} ${itemLabel}` : itemNames[0]}` : ''}
            </div>
          </DialogContent>
          <DialogActions>
            {/* `disabledFocusable` instead of `disabled`: a plain `disabled`
                button is removed from the focus order, so confirming used to
                drop focus to <body> and defeat the dialog's focus trap. */}
            <Button appearance="secondary" onClick={onClose} disabledFocusable={isDeleting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onConfirm}
              className={styles.deleteButton}
              disabledFocusable={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
